import React, { Component } from 'react';
import { array, bool, func, object, string } from 'prop-types';
import { indexOf, remove, sum, uniqBy } from 'ramda';
import Select from 'react-select';

// Components
import AttachmentButton from '../../../AttachmentButton';
import Checkbox from '../../../Checkbox/Checkbox';
import LoadingState from '../../../LoadingState';

import cx from 'classnames';
import s from './MessageForm.css';
import Footer from "../../../Footer/Footer";


export default class MessageForm extends Component {
  static propTypes = {
    authenticatedPreferences: object,
    closeAlertDialog: func,
    forwardMessage: func,
    handleError: func,
    isLoadingForward: bool,
    isLoadingReply: bool,
    isLoadingSendNew: bool,
    handleNewMsgFormDisplay: func,
    handleMsgFormDisplay: func,     // General show/hide handler
    handleUpdateMessageThread: func,
    handleRefreshInbox: func,
    isNewMsg: bool,
    openSuccessDialog: func,
    openErrorDialog: func,
    recipients: array,
    replyMessage: func,
    sendMessage: func,
    thread: object,
    whichForm: string,              // Determines display Reply or Forward UI
  };

  constructor(props) {
    super(props);

    // 20 MB in bytes
    this.maxUploadSize = 20971520;

    this.state = {
      attachments: [],
      attachmentsTotalSize: 0,
      recipients: [],               // internal state when making selections
      fwdSubject: '',               // default subject line
      healthInfoInMsg: false,
    };

    // Practice preferences
    this.messageMultipleSendEnabled = this.props.authenticatedPreferences.messageMultipleSendEnabled;
  }

  // Once we've mounted, we can set the Forward subject line
  componentWillMount() {
    // fwdSubject is not needed for New Messages
    if (!this.props.isNewMsg) {
      this.setState({
        fwdSubject: this.props.thread.subject,
      });
    }
  }

  handleReplySubmit = (e) => {
    e.preventDefault();

    const data = JSON.stringify({
      id: this.props.thread.id,
      reply: this.refs.message.value,
    });

    this.props.replyMessage(
      data,
      this.successCallback,
      this.errorCallback,
    );
  };

  handleFwdSubmit = (e) => {
    e.preventDefault();

    const data = JSON.stringify({
      id: this.props.thread.id,
      to: this.refs.recipient.value,
      subject: this.props.thread.subject,
      message: this.refs.message.value,
    });

    this.props.forwardMessage(
      data,
      this.successCallback,
      this.errorCallback,
    );
  };

  handleNewSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData();
    const files = this.state.attachments;
    const recipientsInState = this.state.recipients;

    /**
     * POR-558: created method to handle messageMultipleSendEnabled preferences
     * based on if true, we must use a non-multi enabled Select.
     * return an array if single recipient.
     */
    const handleRecipients = () => {
      let convertedRecipients;

      if (!this.messageMultipleSendEnabled) {

        const convertedRecipient = parseInt(recipientsInState.value, 10);
        return [convertedRecipient];

      } else {

        // map() over captured recipients, convert the strings to numbers.
        convertedRecipients = recipientsInState.map((rv) => parseInt(rv.value, 10));
        return convertedRecipients;

      }
    };

    // Check if "Add this information to my medical chart."
    const isHealthInfoInMessage = () => this.state.healthInfoInMsg;

    const dataJson = JSON.stringify({
      recipients: handleRecipients(),
      subject: this.refs.subject.value,
      message: this.refs.message.value,
      healthInfoInMessage: isHealthInfoInMessage(),
      location: 0,
    });

    const dataBlob = new Blob([dataJson], { type: 'application/json' });

    // Check for files
    if (files.length >= 1) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        formData.append('files', file);
      }
    }

    formData.append('data', dataBlob);

    this.props.sendMessage(
      formData,
      this.successCallback,
      this.errorCallback,
    );
  };

  handleDialogCloseSuccess = () => {
    if (this.props.isNewMsg) {
      // Hide New Message Form, refresh Inbox
      this.props.handleNewMsgFormDisplay();
      this.props.handleRefreshInbox();
    } else {
      // Fetch the message thread replied or forwarded to update the UI
      this.props.handleMsgFormDisplay(this.props.whichForm);
      this.props.handleUpdateMessageThread(this.props.thread.id);
    }
  };

  handleDialogCloseError = () => {
    if (this.props.isNewMsg) {
      // Hide New Message Form, don't refresh Inbox
      this.props.handleNewMsgFormDisplay();
    }
  };

  successCallback = () =>
    this.props.openSuccessDialog({
      title: 'Message Sent',
      content: 'Your message has been sent. You will be notified when a response is received.',
      okCallback: () => {
        this.props.closeAlertDialog();
        this.handleDialogCloseSuccess();
      },
    });


  errorCallback = response =>
    this.props.openErrorDialog({
      title: 'Error Sending Message',
      content: response.message.body,
      okCallback: () => {
        this.props.closeAlertDialog();
        this.handleDialogCloseError();
      },
    });

  // Update state variable based on what is selected in the react-select <Select />
  handleSelectChange = (recipients) => this.setState({ recipients });

  // Handle updating the value of the Forward subject line
  handleFwdSubjectChange = (e) => this.setState({ fwdSubject: e.target.value });

  // Checked/Unchecked for strategized feature
  handleHealthInfoInMessage = (checked) => this.setState({ healthInfoInMsg: checked });

  // files passed back is an array-like object
  // required a polyfill for Array.from()
  attachFiles = (files) => {

    // Only unique file are attached. If the user as not selected files previously
    // then there is no reason to check for unique files.
    const attachments = !this.state.attachments ? files :
      uniqBy((a) => a.name, [...this.state.attachments, ...files]);
    const attachmentsTotalSize = sum(attachments.map(a => a.size));

    this.setState(prevState => ({
      attachments: attachments,
      attachmentsTotalSize: attachmentsTotalSize,
    }));

  };

  removeFile = (e) => {
    e.preventDefault();

    const fileName = e.target.nextSibling.innerHTML;
    const attachedFileNames = this.state.attachments.map(a => a.name);
    const indexOfFile = indexOf(fileName, attachedFileNames);
    const file = this.state.attachments[indexOfFile];

    this.setState({
      attachments: remove(indexOfFile, 1, this.state.attachments),
      attachmentsTotalSize: this.state.attachmentsTotalSize - file.size
    });
  };

  prettyPrintFileSize = (sizeInBytes) => {
    let size = sizeInBytes;
    if (size < 1024) {
      return size + ' bytes';
    }
    // rounds to a 100th of a KB
    size = Math.round(size / 1024 * 100) / 100;
    if (size < 1024) {
      return size + ' KB';
    }
    // rounds to a 100th of a MB
    size = Math.round(size / 1024 * 100) / 100;
    return size + ' MB';
  };

  // Populate the <Select> with recipients
  renderRecipients = () => {

    if (this.messageMultipleSendEnabled) {

      return (
        <Select
          required
          searchable={true}
          clearable={false}
          id={`${this.props.whichForm}Message__recipients`}
          name={`${this.props.whichForm}Message__recipients`}
          className="composeMessage"
          optionClassName="composeMessage__option"
          ref="recipients"
          placeholder="Select one or more recipients"
          inputProps={{title:'Select Recipients'}}
          options={this.props.recipients}
          value={this.state.recipients}
          onChange={this.handleSelectChange}
          multi
        />
      );

    } else {

      return (
        <Select
          required
          searchable={true}
          clearable={false}
          id={`${this.props.whichForm}Message__recipients`}
          name={`${this.props.whichForm}Message__recipients`}
          className="composeMessage"
          optionClassName="composeMessage__option"
          ref="recipients"
          placeholder="Select one or more recipients"
          inputProps={{title:'Select Recipients'}}
          options={this.props.recipients}
          value={this.state.recipients}
          onChange={this.handleSelectChange}
        />
      );

    }

  };

  renderHealthInfoInMessage = () => {
    const messagePatientHealthInfoEnabled = this.props.authenticatedPreferences.messagePatientHealthInfoEnabled;

    if (messagePatientHealthInfoEnabled) {
      const label = "Add this information to my medical chart.";

      return (
        <div className={cx(s.group, s.actions)}>
          <Checkbox
            checked={this.state.healthInfoInMsg}
            id="healthInfoInMessage"
            onChange={this.handleHealthInfoInMessage}
          />
          <label className={s.healthInfoMsgLabel} htmlFor="healthInfoInMessage">
            Add this information to my medical chart.
          </label>
        </div>
      );
    } else {
      return false;
    }
  };

  render = () => {
    // handleMsgFormDisplay, handleNewMsgFormDisplay closes the form when 'cancel' is clicked
    const {
      handleMsgFormDisplay,
      isNewMsg,
      handleNewMsgFormDisplay,
      whichForm,
    } = this.props;

    // If this is a new message, apply the right click handler, default is for Reply
    // We do the same thing for the submit handler
    const whichCancelHandler = (isNewMsg ? handleNewMsgFormDisplay : handleMsgFormDisplay);
    const whichFormType = (isNewMsg ? 'multipart/form-data' : 'application/x-www-form-urlencoded');

    const messageIsSending = this.props.isLoadingReply || this.props.isLoadingForward || this.props.isLoadingSendNew;

    // Determine which submitHandler we need to use based on passed name value
    // when the button was clicked.
    let submitHandler;
    switch (whichForm) {
      case 'Reply':
        submitHandler = this.handleReplySubmit;
        break;
      case 'Forward':
        submitHandler = this.handleFwdSubmit;
        break;
      case 'New':
        submitHandler = this.handleNewSubmit;
        break;
      default:
        submitHandler = this.handleNewSubmit;
    }

    /*
      Reply is the default UI
    */
    return (
      <section className={s.component}>
        {isNewMsg &&
          <header className={s.msgHeader}>
            New Message
          </header>
        }
        <form
          className={s.form}
          id={`${whichForm}Message-form`}
          onSubmit={submitHandler}
          encType={whichFormType}
        >
          {isNewMsg &&
            <div className={s.group}>
              {this.renderRecipients()}
              <label htmlFor={`${whichForm}Message__subject`} className={s.hidden}>`${whichForm}Message__subject`</label>
              <input
                type="text"
                required
                placeholder="Subject"
                ref="subject"
                maxLength={255}
                name={`${whichForm}Message__subject`}
                id={`${whichForm}Message__subject`}
                className={cx(s.control, s.margins)}
              />
            </div>
          }
          {whichForm === 'Forward' &&
            <div className={s.group}>
              <label htmlFor={`${whichForm}Message__recipient`} className={s.hidden}>`${whichForm}Message__recipient`</label>
              <input
                type="email"
                ref="recipient"
                name={`${whichForm}Message__recipient`}
                id={`${whichForm}Message__recipient`}
                className={s.control}
                required
                placeholder="Enter valid Direct email forwarding address"
              />
              <label htmlFor={`${whichForm}Message__subject`} className={s.hidden}>{whichForm}Message__subject</label>
              <input
                aria-label={`${whichForm}Message__subject`}
                type="text"
                ref="subject"
                name={`${whichForm}Message__subject`}
                id={`${whichForm}Message__subject`}
                className={cx(s.control, s.margins)}
                value={this.state.fwdSubject}
                onChange={this.handleFwdSubjectChange}
              />
            </div>
          }
          <div className={s.group}>
            <label htmlFor={`${whichForm}Message__message`} className={s.hidden}>`${whichForm}Message__message`</label>
            <textarea
              className={cx(s.control, s.textarea)}
              placeholder="Type message here..."
              rows="5"
              ref="message"
              name={`${whichForm}Message__message`}
              id={`${whichForm}Message__message`}
              required
            />
          </div>
          {this.props.isNewMsg && this.renderHealthInfoInMessage()}
          <div className={cx(s.group, s.actions)}>
            <div className={s.messageThreadActions}>
              <button
                type="submit"
                id="send"
                disabled={messageIsSending || this.state.attachmentsTotalSize > this.maxUploadSize}
                className={s.btn}
              >
                {messageIsSending ? (
                  <LoadingState context="msgForm" />
                ) : (
                  'Send'
                )}
              </button>
              <button
                className={cx(s.btn, s.btnSecondary)}
                onClick={whichCancelHandler}
                disabled={messageIsSending}
                id="cancel"
              >
                Cancel
              </button>
              {isNewMsg &&
                <div className={s.attachmentButtonContainer}>
                  <AttachmentButton
                    id={`New-Message__attachFile`}
                    label="Attach a file"
                    name={`New-Message__attachFile`}
                    multiple
                    onChange={this.attachFiles}
                  />
                </div>
              }
            </div>
          </div>
          {isNewMsg && this.state.attachments && this.state.attachments.length > 0 &&
            <div className={s.group}>
              <h2>Attachments: </h2>
              {this.state.attachmentsTotalSize >= this.maxUploadSize &&
                <p className={s.attachmentError}>
                  You have exceeded the combined file size limit of 20 MB. Current combined file size {this.prettyPrintFileSize(this.state.attachmentsTotalSize)}
                </p>
              }
                <ol className={s.attachmentList}>
                  {this.state.attachments.map(attachment =>
                    <li key={attachment.name} className={s.attachmentItem}>
                      <button
                        id={`remove__${attachment.name}`}
                        className={s.remove}
                        onClick={this.removeFile}
                      >
                        X
                      </button>
                      <span>{attachment.name}</span>
                      <span className={s.size}>
                        {this.prettyPrintFileSize(attachment.size)}
                      </span>
                    </li>
                  )}
                </ol>
            </div>
          }
        </form>
        <div className={s.footer}>
          <Footer />
        </div>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Pages/Messages/MessageForm/MessageForm.js